import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { DriverApi, VehicleAssignment, VehicleRecord } from "../driver-api.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  activeAssignment,
  ADD_ANOTHER_VEHICLE_UNAVAILABLE,
  chipIsDesigned,
  classIsVerbatim,
  documentRows,
  documentWordingIsDesigned,
  DOCUMENTS_UNREADABLE,
  NO_ACTIVE_VEHICLE_SUB,
  NO_VEHICLE_SUB,
  NO_VEHICLE_TITLE,
  UPLOAD_DOCUMENT_UNAVAILABLE,
  vehicleChip,
  vehicleSub,
  vehicleTitle,
  VEHICLE_ASSIGNMENT_SCOPE,
  VEHICLE_READ_SCOPE,
} from "../vehicle.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell, type CheckProps } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Vehicle` — the courier's own machine, its papers and the button that adds another.
 *
 * The design: the `idle` hero, the `Offline` pill, the `['done','active_record']` chip,
 * _"Motorcycle T 482 DKX"_, _"Honda CB150 · 2022 · red"_, three document rows, and the two buttons
 * `Upload new roadworthiness` and `Add another vehicle`.
 *
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **buildable with nothing
 * missing** — the one screen of the eighteen whose every absence a single decision closed, which is
 * [ADR 0034](../../../docs/adr/0034-a-drivers-vehicle-is-an-assignment-record-many-over-time.md).
 * `src/vehicle.ts` carries the reasoning for each drawn value and for the two buttons that are drawn
 * and cannot be pressed.
 *
 * ## Two reads, and the second one is allowed to fail on its own
 *
 * The assignment read answers the headline and the sub-line. The vehicle read answers the three
 * document rows and nothing else. They are awaited together and **settled separately**: a courier
 * whose `vehicle:self` was refused still sees which vehicle is theirs, with the three rows replaced
 * by a sentence saying their papers could not be read — rather than an error screen over a plate the
 * application already had, or, far worse, three rows drawn as valid over nothing.
 */

export interface VehicleProps {
  readonly api: DriverApi;
  readonly driverId: string | undefined;
  /** The device's clock, injected so the expiry arithmetic is drivable. Q-65: it is the device's. */
  readonly now: () => number;
}

interface Loaded {
  readonly assignment: VehicleAssignment;
  readonly vehicle: VehicleRecord | undefined;
  /** True when the vehicle read was refused, which is not the same as a vehicle with no documents. */
  readonly documentsUnreadable: boolean;
  /** True when the page came back with rows but none of them active. */
  readonly ended: boolean;
}

export function Vehicle(props: VehicleProps): ReactNode {
  const { api, driverId } = props;
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [empty, setEmpty] = useState<"never" | "ended" | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (driverId === undefined) return;
    setState({ kind: "loading" });
    setEmpty(undefined);
    api
      .vehicleAssignments(driverId)
      .then(async (page) => {
        const assignment = activeAssignment(page.assignments);
        if (assignment === undefined) {
          setLoaded(undefined);
          setEmpty(page.assignments.length === 0 ? "never" : "ended");
          setState({ kind: "ready" });
          return;
        }
        /*
         * The second read, and its failure is not this screen's failure.
         *
         * Caught here rather than by the outer `catch`, so a refused `vehicle:self` leaves the
         * headline drawn and takes only the document rows with it. Without this the whole screen
         * would be an error over a plate the application was already holding.
         */
        let vehicle: VehicleRecord | undefined;
        let unreadable = false;
        try {
          vehicle = await api.vehicle(assignment.vehicle.vehicleId);
        } catch {
          unreadable = true;
        }
        setLoaded({ assignment, vehicle, documentsUnreadable: unreadable, ended: false });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(stateForError(error, [VEHICLE_ASSIGNMENT_SCOPE, VEHICLE_READ_SCOPE]));
      });
  }, [api, driverId]);

  useEffect(load, [load]);

  if (driverId === undefined) {
    /*
     * No courier in the token, which is R-10's refusal to guess one.
     *
     * Nothing on this platform states that a token's subject is a courier's `driverId`, so the
     * application reads the `driver_id` claim and says so when it is absent rather than sending the
     * subject and getting somebody else's vehicle — or, given this read's `:self` arm, a 404 with a
     * misleading cause.
     */
    return (
      <StateScreen
        screen="vehicle"
        chip={{ class: "unknown", label: "no courier named" }}
        title="We don't know which courier you are"
        sub="Your sign-in carries no driver_id, and nothing published says a token's subject is a courier's id, so this app won't guess one."
      />
    );
  }

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="vehicle"
        state={state}
        onRetry={load}
        purpose="Looking up the vehicle assigned to you."
      />
    );
  }

  if (empty !== undefined || loaded === undefined) {
    /*
     * The designed empty state, which the contract calls designed and the design does not draw.
     *
     * DRV-04's listing says a courier with no vehicle "is answered an empty page, because that is a
     * designed state" — and the design file draws no such screen, so the words are this build's and
     * flagged. The two cases are kept apart because they are different facts: nobody has ever
     * assigned a vehicle to this courier, or the one they had has ended.
     */
    return (
      <StateScreen
        screen="vehicle"
        chip={{ class: "waiting", label: "no vehicle" }}
        title={NO_VEHICLE_TITLE}
        sub={empty === "ended" ? NO_ACTIVE_VEHICLE_SUB : NO_VEHICLE_SUB}
      />
    );
  }

  const vehicle = loaded.assignment.vehicle;
  const sub = vehicleSub(vehicle);
  const today = new Date(props.now());
  const documents = loaded.vehicle?.documents ?? [];

  /*
   * The three check rows, or the one sentence that stands in for them.
   *
   * A refused vehicle read is not a vehicle with no papers, and the two must not render alike: the
   * first says "we could not find out" and the second says "there is nothing recorded". Drawing
   * three absence rows for a refused read would be the second sentence over the first fact.
   */
  const checks: readonly CheckProps[] = loaded.documentsUnreadable
    ? [
        {
          key: "documents-unreadable",
          status: "wait",
          /*
           * `pending`, not `lock`. Every check row in both design files takes its glyph from its
           * status and the design's own second array element never reaches the page — Q-130 — so a
           * row drawn with a padlock would be this screen inventing a fifth status glyph.
           */
          glyph: "pending",
          title: "Papers",
          sub: DOCUMENTS_UNREADABLE,
          subIsAbsence: true,
        },
      ]
    : documentRows(documents, today);

  const designed =
    !loaded.documentsUnreadable &&
    sub.designed &&
    chipIsDesigned(vehicle) &&
    classIsVerbatim(vehicle) &&
    documentWordingIsDesigned(documents, today);

  return (
    <PhoneShell
      screen="vehicle"
      /* `art:'idle'` — `box('power_settings_new','Offline')`, untinted, which is the shared disc. */
      art={<HeroDisc glyph="power_settings_new" caption="Offline" />}
      /*
       * `on:0`, the design's own flag on this screen, and it is not an accident of the sample: the
       * vehicle screen is reached off shift. Going online is DRV-05's presence and is not read here,
       * so drawing the pill from the design's flag is drawing what was approved rather than
       * composing a second read to answer a pill.
       */
      online={false}
      earningsLabel={null}
      chip={vehicleChip(vehicle)}
      title={vehicleTitle(vehicle)}
      sub={sub.text}
      checks={checks}
      actions={[
        {
          label: "Upload new roadworthiness",
          glyph: "upload",
          primary: true,
          unavailable: UPLOAD_DOCUMENT_UNAVAILABLE,
        },
        {
          label: "Add another vehicle",
          glyph: "add",
          primary: false,
          unavailable: ADD_ANOTHER_VEHICLE_UNAVAILABLE,
        },
      ]}
      {...(designed ? {} : { flag: "wording not in the design" })}
    />
  );
}
